import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Empty } from "../components/UI";
import { useRxPanels } from "../components/rx/useRxPanels";
import { useAuth } from "../context/AuthContext";
import { RxCallForm } from "../components/rx/RxCommForms";
import { fmtDate, fmtDateTime } from "../components/rx/rxShared";
import { NewPill } from "../components/NewPill";
import { PhoneCall, Phone, RefreshCw, CircleSlash } from "lucide-react";
import dayjs from "dayjs";

const waited = (d) => { const n = dayjs().diff(dayjs(d), "day"); return n <= 0 ? "today" : `${n} day${n === 1 ? "" : "s"} ago`; };

function QueueRow({ r, meta, calling, onCall, onCancel, onDone, onOpen }) {
  const last = r.last_attempt;
  return (
    <div className="border-b border-[#F1EEE7] px-4 py-3 last:border-0" data-testid={`refill-row-${r.line_id}`}>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-[1.1fr_1.5fr_1.2fr_1fr_auto] md:items-center">
        <div><Link to={`/clients/${r.client_id}`} data-testid={`refill-client-${r.line_id}`} className="text-sm font-semibold text-[#3E2723] hover:underline">{r.client_name}</Link>
          <div className="font-mono text-[10px] text-[#84675B]">{r.rx_number} · {r.prescriber_name}</div></div>
        <div><div className="text-sm font-semibold text-[#5D4037]">{r.medication}</div>
          <div className="text-[11px] text-[#7B6157]">Refills on the script: {r.refills || "0"}</div>
          <div className="flex items-start gap-1 text-[11px] text-[#C62828]"><CircleSlash size={10} className="mt-0.5 shrink-0" /> Discontinued {fmtDate(r.discontinued_at)} ({waited(r.discontinued_at)}) by {r.discontinued_by}{r.reason ? `: ${r.reason}` : ""}</div></div>
        <div className="text-sm text-[#5D4037]"><div className="font-semibold">{r.pharmacy.name}</div>
          {r.pharmacy.phone ? <a href={`tel:${r.pharmacy.phone}`} data-testid={`refill-phone-${r.line_id}`} className="flex items-center gap-1 text-xs text-[#4E342E] hover:underline"><Phone size={11} /> {r.pharmacy.phone}</a> : <span className="text-xs text-[#84675B]">No phone on file</span>}</div>
        <div className="text-[11px] text-[#7B6157]" data-testid={`refill-attempts-${r.line_id}`}>
          <div className="font-semibold text-[#5D4037]">{r.attempts ? `${r.attempts} call attempt${r.attempts === 1 ? "" : "s"}` : "Not called yet"}</div>
          {last && <div>{last.outcome} · {fmtDateTime(last.at)} · {last.staff_name}</div>}</div>
        <div className="flex gap-2 md:justify-end">
          <button type="button" data-testid={`refill-call-${r.line_id}`} onClick={onCall} className="flex items-center gap-1 rounded-full bg-[#4E342E] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#3E2723]"><PhoneCall size={12} /> Log call</button>
          <button type="button" data-testid={`refill-open-${r.line_id}`} onClick={() => onOpen(r.rx_id)} className="rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-semibold text-[#4E342E] transition-colors hover:bg-[#F5F2EB]">Open</button>
        </div>
      </div>
      {calling && meta && <div className="mt-3"><RxCallForm rx={{ id: r.rx_id }} group={{ pharmacy: r.pharmacy, line_ids: [r.line_id], active_line_ids: [] }} outcomes={meta.call_outcomes}
        initialOutcome="Remaining refills cancelled" onCancel={onCancel} onDone={onDone} /></div>}
    </div>
  );
}

/** Refill Call Queue: discontinued medications whose remaining refills still need a cancel call to the pharmacy. */
export default function RefillCallQueue() {
  const { user } = useAuth();
  if (!["admin", "clinician"].includes(user?.role) && !user?.is_super_admin) {
    return (
      <div data-testid="refill-call-queue-denied">
        <PageHeader title="Refill Call Queue" subtitle="Pharmacy refill cancel calls" />
        <Card className="p-8 text-center"><p className="font-heading text-base font-bold text-[#3E2723]">This queue is only for clinicians and admins.</p>
          <p className="mt-1 text-sm text-[#7B6157]">Ask an admin if you need to log pharmacy calls.</p>
          <Link to="/" data-testid="refill-denied-home" className="mt-4 inline-block rounded-full border border-[#E5DFD3] px-4 py-2 text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB]">Go to the dashboard</Link></Card>
      </div>
    );
  }
  return <RefillQueueBoard />;
}

function RefillQueueBoard() {
  const [data, setData] = useState(null);
  const [calling, setCalling] = useState(null);
  const load = useCallback(() => api.get("/prescriptions/refill-call-queue").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  const { panels, openRx, meta } = useRxPanels(load);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="refill-call-queue">
      <PageHeader title={<span className="inline-flex items-center gap-2">Refill Call Queue <NewPill feature="refill-call-queue" /></span>}
        subtitle="Discontinued medications that may still have refills at the pharmacy. Call to cancel them, then log the call with the outcome Remaining refills cancelled."
        action={<button type="button" data-testid="refill-refresh" onClick={load} className="flex items-center gap-1.5 rounded-full border border-[#E5DFD3] bg-white px-4 py-2 text-sm font-semibold text-[#4E342E] hover:bg-[#F5F2EB]"><RefreshCw size={14} /> Refresh</button>} />
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-[#EFEBE0] bg-[#FDFCF8] px-4 py-3">
          <h2 className="font-heading text-base font-bold text-[#3E2723]">Waiting for a cancel call</h2>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${data?.total ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#EFEBE0] text-[#6D4C41]"}`} data-testid="refill-total">{data ? data.total : "-"}</span>
        </div>
        {!data ? <div className="m-4 h-24 animate-pulse rounded-xl bg-[#F5F2EB]" /> : data.items.length ? data.items.map((r) => (
          <QueueRow key={r.line_id} r={r} meta={meta} calling={calling === r.line_id} onCall={() => setCalling(r.line_id)} onCancel={() => setCalling(null)}
            onDone={() => { setCalling(null); load(); }} onOpen={openRx} />
        )) : <Empty text="No discontinued medications are waiting for a refill cancel call." />}
      </Card>
      {panels}
    </div>
  );
}
